/**
 * Part 1 — TurboTax-style "My Tasks" guided progression widget.
 *
 * Fetches GET /api/enrichment/my-tasks and renders the signed-in user's
 * role-specific, sequential to-do list with status badges (Not Started /
 * In Progress / Done) and enrichment-derived context hints. Hidden when the
 * role has no defined task track.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { CheckCircle2, Circle, CircleDot, ChevronRight, ListChecks, RefreshCw } from "lucide-react";

const STATUS_META = {
  done: { label: "Done", icon: CheckCircle2, badge: "bg-emerald-100 text-emerald-700", dot: "text-emerald-500" },
  in_progress: { label: "In Progress", icon: CircleDot, badge: "bg-amber-100 text-amber-700", dot: "text-amber-500" },
  not_started: { label: "Not Started", icon: Circle, badge: "bg-slate-100 text-slate-500", dot: "text-slate-300" },
};

export default function MyTasksWidget() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await api.get("/enrichment/my-tasks");
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); /* eslint-disable-next-line */ }, []);

  if (loading && !data) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="my-tasks-widget-loading">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading your tasks...
        </div>
      </div>
    );
  }

  if (!data || !data.tasks || data.tasks.length === 0) return null;

  const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;
  // The first not-started/in-progress task is the user's "next step".
  const nextIdx = data.tasks.findIndex((t) => t.status !== "done");

  return (
    <div className="bg-white border border-sky-200 rounded-xl shadow-sm overflow-hidden" data-testid="my-tasks-widget">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-sky-50/50">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-700 to-teal-600 flex items-center justify-center shadow-sm">
            <ListChecks className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              My Tasks
            </h3>
            <p className="text-[10px] text-slate-400">
              {data.completed} of {data.total} complete · your guided next steps
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-bold text-emerald-600" data-testid="my-tasks-pct">{pct}%</span>
          </div>
          <button onClick={fetchTasks} className="text-sky-600 hover:text-sky-700 p-1" data-testid="my-tasks-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {data.tasks.map((t, i) => {
          const meta = STATUS_META[t.status] || STATUS_META.not_started;
          const Icon = meta.icon;
          const isNext = i === nextIdx;
          return (
            <Link
              key={t.id}
              to={t.link || "/dashboard"}
              className={`flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors ${isNext ? "bg-sky-50/40" : ""}`}
              data-testid={`my-task-${t.id}`}
            >
              <Icon className={`w-5 h-5 shrink-0 ${meta.dot}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-medium ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-800"}`}>
                    {i + 1}. {t.title}
                  </span>
                  {isNext && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-sky-600 text-white uppercase tracking-wide" data-testid="my-tasks-next-badge">Next</span>}
                  {typeof t.count === "number" && t.count > 0 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{t.count}</span>
                  )}
                </div>
                {t.hint && <p className="text-[11px] text-slate-500 truncate mt-0.5">{t.hint}</p>}
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${meta.badge}`}>{meta.label}</span>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
